import { apiClient } from './api';

export interface AppointmentServiceItem {
  serviceId: string;
  serviceName: string;
  duration: number;
  price: number;
}

export interface ApiAppointment {
  id: string;
  customerId: string;
  staffId: string;
  services: AppointmentServiceItem[] | any;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  status: 'SCHEDULED' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  totalAmount: number;
  notes?: string | null;
  customer?: {
    id: string;
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string | null;
  };
  staff?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface Appointment {
  id: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  customerId: string;
  staffId: string;
  serviceName: string;
  services: AppointmentServiceItem[];
  appointmentDate: string;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  staffName: string;
  totalAmount: number;
  notes?: string;
  createdAt: string;
}

export interface CreateAppointmentData {
  customerId: string;
  staffId: string;
  services: AppointmentServiceItem[];
  appointmentDate: string;
  startTime: string;
  notes?: string;
}

export interface UpdateAppointmentData {
  customerId?: string;
  staffId?: string;
  services?: AppointmentServiceItem[];
  appointmentDate?: string;
  startTime?: string;
  status?: string;
  notes?: string;
}

export interface AppointmentsResponse {
  appointments: Appointment[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AppointmentFilters {
  customerId?: string;
  staffId?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

function mapApiAppointmentToFrontend(apt: ApiAppointment): Appointment {
  const services = Array.isArray(apt.services)
    ? apt.services
    : typeof apt.services === 'object'
    ? Object.values(apt.services as Record<string, AppointmentServiceItem>)
    : [];

  const firstService = services[0];
  const customerName = apt.customer
    ? `${apt.customer.firstName} ${apt.customer.lastName}`.trim()
    : 'Unknown';
  const staffName = apt.staff
    ? `${apt.staff.firstName} ${apt.staff.lastName}`.trim()
    : 'Unknown';

  const statusMap: Record<string, Appointment['status']> = {
    SCHEDULED: 'scheduled',
    CONFIRMED: 'confirmed',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
  };

  return {
    id: apt.id,
    customerId: apt.customerId,
    staffId: apt.staffId,
    customerName,
    customerPhone: apt.customer?.phone,
    customerEmail: apt.customer?.email ?? undefined,
    serviceName: firstService?.serviceName ?? 'Service',
    services,
    appointmentDate:
      typeof apt.appointmentDate === 'string'
        ? apt.appointmentDate.split('T')[0]
        : new Date(apt.appointmentDate).toISOString().split('T')[0],
    startTime: apt.startTime,
    endTime: apt.endTime,
    status: statusMap[apt.status] ?? 'scheduled',
    staffName,
    totalAmount: Number(apt.totalAmount ?? 0),
    notes: apt.notes ?? undefined,
    createdAt: (apt as any).createdAt ?? new Date().toISOString(),
  };
}

class AppointmentService {
  private baseUrl = '/appointments';

  async getAppointments(filters: AppointmentFilters = {}): Promise<AppointmentsResponse> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiClient.get<{
      data: { appointments: ApiAppointment[] };
      pagination?: any;
    }>(`${this.baseUrl}?${params.toString()}`);

    const apiAppointments = response?.data?.appointments ?? [];
    const appointments = apiAppointments.map(mapApiAppointmentToFrontend);

    return {
      appointments,
      pagination: response?.pagination,
    };
  }

  async getAppointmentById(id: string): Promise<Appointment | null> {
    const response = await apiClient.get<{ data: { appointment: ApiAppointment } }>(
      `${this.baseUrl}/${id}`
    );
    const apt = response?.data?.appointment;
    if (!apt) return null;
    return mapApiAppointmentToFrontend(apt);
  }

  async createAppointment(data: CreateAppointmentData): Promise<Appointment> {
    const payload = {
      ...data,
      appointmentDate: new Date(data.appointmentDate),
    };
    const response = await apiClient.post<{ data: { appointment: ApiAppointment } }>(
      this.baseUrl,
      payload
    );
    const apt = (response as any)?.data?.appointment ?? (response as any)?.appointment ?? response;
    return mapApiAppointmentToFrontend(apt as ApiAppointment);
  }

  async updateAppointment(id: string, data: UpdateAppointmentData): Promise<Appointment> {
    const payload = data.appointmentDate
      ? { ...data, appointmentDate: new Date(data.appointmentDate) }
      : data;
    const response = await apiClient.put<{ data: { appointment: ApiAppointment } }>(
      `${this.baseUrl}/${id}`,
      payload
    );
    const apt = (response as any)?.data?.appointment ?? (response as any)?.appointment ?? response;
    return mapApiAppointmentToFrontend(apt as ApiAppointment);
  }

  async updateAppointmentStatus(
    id: string,
    status: Appointment['status']
  ): Promise<Appointment> {
    const statusMap: Record<string, string> = {
      scheduled: 'SCHEDULED',
      confirmed: 'CONFIRMED',
      in_progress: 'IN_PROGRESS',
      completed: 'COMPLETED',
      cancelled: 'CANCELLED',
    };
    return this.updateAppointment(id, {
      status: statusMap[status] ?? status.toUpperCase(),
    });
  }

  async confirmAppointment(id: string): Promise<Appointment> {
    const response = await apiClient.post<{ data: { appointment: ApiAppointment } }>(
      `${this.baseUrl}/${id}/confirm`
    );
    const apt = (response as any)?.data?.appointment ?? (response as any)?.appointment ?? response;
    return mapApiAppointmentToFrontend(apt as ApiAppointment);
  }

  async completeAppointment(id: string): Promise<Appointment> {
    const response = await apiClient.post<{ data: { appointment: ApiAppointment } }>(
      `${this.baseUrl}/${id}/complete`
    );
    const apt = (response as any)?.data?.appointment ?? (response as any)?.appointment ?? response;
    return mapApiAppointmentToFrontend(apt as ApiAppointment);
  }

  async cancelAppointment(id: string, reason?: string): Promise<Appointment> {
    const response = await apiClient.post<{ data: { appointment: ApiAppointment } }>(
      `${this.baseUrl}/${id}/cancel`,
      reason ? { reason } : {}
    );
    const apt = (response as any)?.data?.appointment ?? (response as any)?.appointment ?? response;
    return mapApiAppointmentToFrontend(apt as ApiAppointment);
  }

  async deleteAppointment(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }
}

export const appointmentService = new AppointmentService();
